<template>
  <div class="company-settings-page">
    <!-- 顶部操作栏 -->
    <div class="top-header">
      <h2>企业基础设置</h2>
      <template v-if="isDesktop">
        <el-radio-group v-model="currentLang" @change="handleLangChange">
          <el-radio-button v-for="item in languageList" :key="item.code" :label="item.code">{{ item.name }}</el-radio-button>
        </el-radio-group>
        <el-button type="primary" @click="handleSaveAll">保存全部</el-button>
      </template>
      <template v-else>
        <van-radio-group v-model="currentLang" @change="handleLangChange">
          <van-radio v-for="item in languageList" :key="item.code" :label="item.code">{{ item.name }}</van-radio>
        </van-radio-group>
      </template>
    </div>

    <!-- 内容区域 -->
    <div class="content-container" v-loading="loading">
      <!-- PC 端表单 -->
      <div v-if="isDesktop" class="settings-card">
        <div class="settings-section" v-for="group in settingGroups" :key="group.key">
          <div class="section-title">
            <i :class="group.icon"></i>
            <span>{{ group.label }}</span>
          </div>
          <div class="section-body">
            <div class="form-row" v-for="field in group.fields" :key="field.key">
              <div class="form-label">{{ field.label }}</div>
              <div class="form-control">
                <!-- 图片上传 -->
                <template v-if="field.type === 'image'">
                  <div class="image-upload-area" @click="triggerUpload(field.key)">
                    <img v-if="formData[field.key]" :src="formData[field.key]" class="preview-image" />
                    <div v-else class="upload-placeholder">
                      <i class="el-icon-plus"></i>
                      <span>上传图片</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    :ref="'fileInput_' + field.key"
                    accept="image/*"
                    style="display:none"
                    @change="handleImageUpload($event, field.key)"
                  />
                  <el-button v-if="formData[field.key]" type="text" size="small" @click="clearImage(field.key)" style="margin-top:4px;">清除图片</el-button>
                </template>
                <!-- 普通输入框 -->
                <template v-else>
                  <el-input v-model="formData[field.key]" :placeholder="'请输入' + field.label" />
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="save-bar">
          <el-button type="primary" @click="handleSaveAll" :loading="saving">保存全部设置</el-button>
        </div>
      </div>

      <!-- 移动端表单 -->
      <div v-else class="mobile-settings">
        <van-cell-group v-for="group in settingGroups" :key="group.key" inset style="margin-bottom:16px;">
          <div class="mobile-section-title">
            <i :class="group.icon"></i>
            <span>{{ group.label }}</span>
          </div>
          <template v-for="field in group.fields">
            <!-- 图片上传 -->
            <van-cell v-if="field.type === 'image'" :key="field.key" :title="field.label" is-link>
              <template #default>
                <div class="mobile-image-wrapper">
                  <img v-if="formData[field.key]" :src="formData[field.key]" class="mobile-preview-image" />
                  <span v-else class="mobile-no-image">未设置</span>
                </div>
              </template>
              <template #right-icon>
                <div></div>
              </template>
            </van-cell>
            <!-- 普通输入 -->
            <van-field
              v-else
              :key="field.key"
              :label="field.label"
              v-model="formData[field.key]"
              :placeholder="'请输入' + field.label"
            />
          </template>
        </van-cell-group>
        <div class="mobile-save-bar">
          <van-button type="primary" block @click="handleSaveAll" :loading="saving">保存全部设置</van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLanguage } from '@/api/public'
import { getCompanySettingsByLang, getCompanySettings, updateCompanySetting } from '@/api/website'
import { getQiniuToken } from '@/api/public'

export default {
  name: 'CompanySettingsPage',
  data() {
    return {
      isDesktop: true,
      languageList: [],
      currentLang: 'zh',
      loading: false,
      saving: false,
      formData: {
        company_name: '',
        address: '',
        image: '',
        sales_email: ''
      },
      // 存储每条记录的 id，用于更新
      idMap: {},
      settingGroups: [
        {
          key: 'basic',
          label: '基本信息',
          icon: 'el-icon-office-building',
          fields: [
            { key: 'company_name', label: '公司名称', type: 'text' },
            { key: 'address', label: '公司地址', type: 'text' },
            { key: 'sales_email', label: '销售邮箱', type: 'text' }
          ]
        },
        {
          key: 'media',
          label: '媒体资源',
          icon: 'el-icon-picture-outline',
          fields: [
            { key: 'image', label: '公司图片', type: 'image' }
          ]
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize)
    this.getLanguageList()
    this.loadSettings()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    async getLanguageList() {
      try {
        const response = await getLanguage()
        this.languageList = response.data || []
      } catch (error) {
        console.error('获取语言列表失败:', error)
      }
    },
    loadSettings() {
      this.loading = true
      getCompanySettingsByLang(this.currentLang).then(res => {
        if (res.code === 200) {
          // res.data 是 { company_name: 'xxx', address: 'xxx', ... } 格式
          // 但我们需要 id 来更新，所以先获取完整列表
          this.fetchFullList()
        }
      }).catch(() => {
        this.loading = false
      })
    },
    fetchFullList() {
      var vm = this
      getCompanySettings().then(function(res) {
        if (res.code === 200) {
          var list = res.data || []
          var idMap = {}
          var formData = {
            company_name: '',
            address: '',
            image: '',
            sales_email: ''
          }
          list.forEach(function(item) {
            if (item.language_code === vm.currentLang) {
              idMap[item.title] = item.id
              formData[item.title] = item.content || ''
            }
          })
          vm.idMap = idMap
          vm.formData = formData
        }
        vm.loading = false
      }).catch(function() {
        vm.loading = false
      })
    },
    handleLangChange() {
      this.loadSettings()
    },
    handleSaveAll() {
      var vm = this
      vm.saving = true
      var promises = []
      var fields = Object.keys(vm.formData)

      fields.forEach(function(key) {
        if (!vm.idMap[key]) return
        promises.push(
          updateCompanySetting(vm.idMap[key], { content: vm.formData[key] })
        )
      })

      Promise.all(promises).then(function() {
        vm.$message.success('保存成功')
        vm.saving = false
      }).catch(function() {
        vm.$message.error('保存失败')
        vm.saving = false
      })
    },
    triggerUpload(fieldKey) {
      var refName = 'fileInput_' + fieldKey
      var input = this.$refs[refName]
      if (input && input[0]) {
        input[0].click()
      } else if (input) {
        input.click()
      }
    },
    handleImageUpload(event, fieldKey) {
      var vm = this
      var file = event.target.files[0]
      if (!file) return

      getQiniuToken().then(function(res) {
        if (res.code !== 200) {
          vm.$message.error('获取上传凭证失败')
          return
        }
        var token = res.data
        var ext = file.name.substring(file.name.lastIndexOf('.') + 1)
        var key = 'company/' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + ext

        var formData = new FormData()
        formData.append('file', file)
        formData.append('token', token)
        formData.append('key', key)

        fetch('https://up-z0.qiniup.com/', {
          method: 'POST',
          body: formData
        }).then(function(response) { return response.json() }).then(function(data) {
          if (data.key) {
            var cdnUrl = 'https://cdn.puditech.com/' + data.key
            vm.$set(vm.formData, fieldKey, cdnUrl)
            vm.$message.success('上传成功')
          } else {
            vm.$message.error('上传失败')
          }
        }).catch(function() {
          vm.$message.error('上传失败')
        })
      })
    },
    clearImage(fieldKey) {
      this.$set(this.formData, fieldKey, '')
    }
  }
}
</script>

<style scoped lang="scss">
.company-settings-page {
  width: 100%;
  padding: 20px;
}

.top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border, #e4e7ed);
}

.top-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text, #303133);
}

.content-container {
  min-height: 400px;
}

/* PC 端 */
.settings-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.settings-section {
  padding: 0;
}

.settings-section + .settings-section {
  border-top: 1px solid var(--border-light, #ebeef5);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: var(--bg-100, #f5f7fa);
  font-size: 15px;
  font-weight: 600;
  color: var(--text, #303133);
}

.section-title i {
  font-size: 18px;
  color: #409EFF;
}

.section-body {
  padding: 16px 24px;
}

.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  width: 100px;
  flex-shrink: 0;
  padding-top: 8px;
  font-size: 14px;
  color: var(--text-200, #606266);
  font-weight: 500;
}

.form-control {
  flex: 1;
  min-width: 0;
}

.image-upload-area {
  width: 200px;
  height: 120px;
  border: 2px dashed var(--border, #dcdfe6);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s;
}

.image-upload-area:hover {
  border-color: #409EFF;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: var(--text-200, #909399);
  font-size: 13px;
}

.upload-placeholder i {
  font-size: 28px;
}

.save-bar {
  padding: 20px 24px;
  border-top: 1px solid var(--border-light, #ebeef5);
  text-align: right;
}

/* 移动端 */
.mobile-settings {
  padding-bottom: 80px;
}

.mobile-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text, #303133);
  background: var(--bg-100, #f5f7fa);
}

.mobile-section-title i {
  font-size: 16px;
  color: #409EFF;
}

.mobile-image-wrapper {
  width: 48px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
}

.mobile-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-no-image {
  font-size: 12px;
  color: var(--text-200, #909399);
}

.mobile-save-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  z-index: 100;
}

/* 响应式 */
@media (max-width: 768px) {
  .company-settings-page {
    padding: 10px;
  }

  .top-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
